import {
  ILeaderboardData,
  ILeaderboardOptInHistory, IUserDataForMemberManagement, IUserLeaderboardPreferenceViewResult,
} from 'fitbeat.models';
import moment from 'moment-timezone';

function didOptInOnSameDay(
  currentEntry: ILeaderboardOptInHistory,
  previousEntry: ILeaderboardOptInHistory | undefined,
  timezone: string,
): boolean {
  if (previousEntry && previousEntry.optOutTime) {
    const momentPreviousEntryOptOutTime = moment.tz(previousEntry.optOutTime, timezone);
    const momentOptIntTime = moment.tz(currentEntry.optInTime, timezone);
    if (momentPreviousEntryOptOutTime.isSame(momentOptIntTime, 'day')) {
      return true;
    }
  }
  return false;
}

export const getDaysOptedIntoTeamLeaderboard = (
  today: Date,
  startDateOfLeaderboard: Date,
  endDateOfLeaderboard: Date,
  timezone: string,
  optInHistory: ILeaderboardOptInHistory[],
  team: string,
): number | undefined => {
  const applicableOptInHistory: ILeaderboardOptInHistory[] = optInHistory.filter((entry: ILeaderboardOptInHistory) => {
    const latestOptInHistoryEventDate: Date | undefined = entry.optOutTime || entry.optInTime;

    if (!latestOptInHistoryEventDate) {
      return false;
    }

    return latestOptInHistoryEventDate < endDateOfLeaderboard && entry.team === team;
  });

  let numberOfDays: number | undefined;
  applicableOptInHistory.forEach((entry: ILeaderboardOptInHistory, index) => {
    if (entry.optInTime) {
      const hasOptOutTime: Date | undefined = entry.optOutTime || applicableOptInHistory[index + 1]?.optOutTime;

      const earliestStart = entry.optInTime >= startDateOfLeaderboard ? entry.optInTime : startDateOfLeaderboard;
      if (hasOptOutTime) {
        const days = countWholeDays(earliestStart, hasOptOutTime, timezone);
        numberOfDays = numberOfDays ? numberOfDays + days : days;
      } else {
        const dateToUseAsEndDate = today > endDateOfLeaderboard ? endDateOfLeaderboard : today;
        const days = countWholeDays(earliestStart, dateToUseAsEndDate, timezone);
        numberOfDays = numberOfDays ? numberOfDays + days : days;
      }

      const previousEntry = applicableOptInHistory[index - 1];
      if (didOptInOnSameDay(entry, previousEntry, timezone) && entry.optInTime >= startDateOfLeaderboard) {
        numberOfDays = Math.max(numberOfDays - 1, 0);
      }
    }
  });

  return numberOfDays;
};

export const getDaysIntoChallenge = (today: Date, startDate: Date, endDate: Date, timezone: string): number => {
  if (today > endDate) {
    return countWholeDays(startDate, endDate, timezone);
  }

  return countWholeDays(startDate, today, timezone);
};

const countWholeDays = (startDate: Date, endDate: Date, timezone: string): number => {
  const startMoment = moment.tz(startDate, timezone);
  const endMoment = moment.tz(endDate, timezone);
  startMoment.startOf('day');
  endMoment.startOf('day');

  const diffInDays = endMoment.diff(startMoment, 'days') + 1;

  return Math.max(diffInDays, 0);
};

export const mapLeaderboardPreferencesToTeams = (singleUserProfile: IUserDataForMemberManagement) => {
  const allTeams = [];
  const leaderboardPreferences = singleUserProfile.leaderboardPreferences?.leaderboardPreferences;
  if (leaderboardPreferences) {
    for (const item in leaderboardPreferences) {
      if (leaderboardPreferences.hasOwnProperty(item)) {
        const { optInHistory } = leaderboardPreferences[item];
        const lastItemInList = optInHistory[optInHistory.length - 1];
        const hasOptOut = lastItemInList.optOutTime;
        if (!hasOptOut && lastItemInList?.team) {
          allTeams.push(lastItemInList.team);
        }
      }
    }
  }
  return allTeams;
};

export function getUsersWithActiveLeaderboardPreference(
  usersWithLeaderboardPreferences: IUserLeaderboardPreferenceViewResult[],
  leaderboardData: ILeaderboardData,
) {
  return usersWithLeaderboardPreferences.reduce((
    acc: IUserLeaderboardPreferenceViewResult[],
    leaderboardPreference: IUserLeaderboardPreferenceViewResult,
  ) => {
    const { optInHistory } = leaderboardPreference.leaderboardPreference;
    const lastOptOutDate = optInHistory[optInHistory.length - 1].optOutTime;
    if (!lastOptOutDate || lastOptOutDate >= leaderboardData.startDate) {
      acc.push(leaderboardPreference);
    }
    return acc;
  }, []);
}

export function getCountTotalMembersPerTeam(
  usersWithLeaderboardPreferences: IUserLeaderboardPreferenceViewResult[],
  countZeroScores: boolean,
) {
  const totalMembersPerTeam: Record<string, number> = {};

  usersWithLeaderboardPreferences.forEach((
    leaderboardPreference: IUserLeaderboardPreferenceViewResult,
  ) => {
    const { optInHistory } = leaderboardPreference.leaderboardPreference;
    const { team } = optInHistory[optInHistory.length - 1];
    if (countZeroScores) {
      if (team) {
        totalMembersPerTeam[team] = (totalMembersPerTeam[team] ?? 0) + 1;
      }
    }
  }, []);

  return totalMembersPerTeam;
}

export function getCurrentMembersInTeam(
  usersWithLeaderboardPreferences: IUserLeaderboardPreferenceViewResult[],
  teamName?: string,
) {
  if (!teamName) {
    return 0;
  }
  const usersInTeamWithNoOptOutTime = usersWithLeaderboardPreferences.reduce((count, userProfile) => {
    const { optInHistory } = userProfile.leaderboardPreference;
    const lastEntry = optInHistory[optInHistory.length - 1];

    if (lastEntry.team === teamName && !lastEntry.optOutTime) {
      return count + 1;
    }

    return count;
  }, 0);
  return usersInTeamWithNoOptOutTime;
}
